// Video categories
export type VideoCategory = 'getting-started' | 'section-guide' | 'tips-tricks'

// Video metadata type
export type OnboardingVideo = {
  id: string
  title: string
  description: string
  playbackId: string
  duration: string // Format: "M:SS" or "MM:SS"
  category: VideoCategory
}

// All onboarding videos with Mux playback IDs
export const ONBOARDING_VIDEOS: OnboardingVideo[] = [
  // Getting Started videos (shown in first-time onboarding)
  {
    id: 'getting-started',
    title: 'Get to know Homie',
    description: 'A quick tour of the app',
    playbackId: 'qHO3lhduq2ohtS4NMChgeiMG26XkP01JAf7g02pFP02ibo',
    duration: '3:17',
    category: 'getting-started'
  },
  {
    id: 'family-coordination',
    title: 'Organize your family',
    description: 'How Homie simplifies family life',
    playbackId: 'cbN5toLPtTQB7YtifRCB02SE01RFhtHohrcfhvxq8LARU',
    duration: '2:08',
    category: 'getting-started'
  },
  {
    id: 'import-calendars',
    title: 'Calendar setup and import',
    description: 'Easily import your old calendars',
    playbackId: 'VbISzm1OszFL4qciYXvqV3v01KEf4P5YtSl0102gznv9sA',
    duration: '4:55',
    category: 'getting-started'
  },
  {
    id: 'customize-settings',
    title: 'Customize the app',
    description: 'Make Homie work your way',
    playbackId: 'Zjd6r02vvRHRv02vwKj2pSpYctdf8sPdbp3Ax2ISoW7U00',
    duration: '4:59',
    category: 'getting-started'
  },

  // Section Guide videos (per-view helpers)
  {
    id: 'agenda',
    title: 'Agenda',
    description: 'Your daily overview',
    playbackId: 'YCY7BIEa2z800KZWMqgebOxE001JF601AhGAb2I02IiN8W4',
    duration: '0:49',
    category: 'section-guide'
  },
  {
    id: 'calendar',
    title: 'Calendar',
    description: 'Managing your events',
    playbackId: 'A2ltTztmr02yDZMWP2tMPPN1auRJihOczJxZF1qWX5Ms',
    duration: '2:15',
    category: 'section-guide'
  },
  {
    id: 'meals',
    title: 'Meals',
    description: 'Planning your meals',
    playbackId: '5LewcF02y35yQlvGhaNU4W01kxh02NFORKNL019KFrTSZGM',
    duration: '2:34',
    category: 'section-guide'
  },
  {
    id: 'routines',
    title: 'Routines',
    description: 'Building daily habits',
    playbackId: 'g5I1ec3zwQK1ZX9acoUv91kppP5YE01T1bIWdNV8xhzU',
    duration: '1:29',
    category: 'section-guide'
  },
  {
    id: 'tasks',
    title: 'Tasks',
    description: 'Managing your to-dos',
    playbackId: 'sWtgQNPbVvNliM01MUTxlyZSjKNkKWCgZsKqz2aP7XYA',
    duration: '2:25',
    category: 'section-guide'
  },

  // Tips & Tricks videos
  {
    id: 'sharing',
    title: 'Sharing',
    description: 'Task lists, events, recipes, ..',
    playbackId: '00S01AMp9OZg38v00YxO01IDFaXJZb8koEQhnVCKNPpTu0100',
    duration: '3:02',
    category: 'tips-tricks'
  }
]

// Helper functions
export const getVideoById = (id: string): OnboardingVideo | undefined =>
  ONBOARDING_VIDEOS.find(v => v.id === id)

export const getGettingStartedVideos = (): OnboardingVideo[] =>
  ONBOARDING_VIDEOS.filter(v => v.category === 'getting-started')

export const getSectionGuideVideos = (): OnboardingVideo[] =>
  ONBOARDING_VIDEOS.filter(v => v.category === 'section-guide')

export const getTipsTricksVideos = (): OnboardingVideo[] =>
  ONBOARDING_VIDEOS.filter(v => v.category === 'tips-tricks')

export const getHelperVideoForSection = (section: string): OnboardingVideo | undefined =>
  ONBOARDING_VIDEOS.find(v => v.id === section && v.category === 'section-guide')

// Get thumbnail URL for a video
export const getVideoThumbnail = (playbackId: string, time = 0): string =>
  `https://image.mux.com/${playbackId}/thumbnail.webp?time=${time}`

// Get animated GIF preview for a video
export const getVideoGif = (playbackId: string, start = 0, end = 5): string =>
  `https://image.mux.com/${playbackId}/animated.gif?start=${start}&end=${end}&width=320`

// Get Mux stream URL for a video
export const getVideoStreamUrl = (playbackId: string): string =>
  `https://stream.mux.com/${playbackId}.m3u8`

// Convert duration string (M:SS or MM:SS) to ISO 8601 duration format (PT#M#S)
export const durationToISO8601 = (duration: string): string => {
  const parts = duration.split(':')
  const minutes = parseInt(parts[0], 10)
  const seconds = parseInt(parts[1], 10)
  return `PT${minutes}M${seconds}S`
}

// Convert duration string to total seconds
export const durationToSeconds = (duration: string): number => {
  const parts = duration.split(':')
  const minutes = parseInt(parts[0], 10)
  const seconds = parseInt(parts[1], 10)
  return minutes * 60 + seconds
}

// Get all videos (for schema generation)
export const getAllVideos = (): OnboardingVideo[] => ONBOARDING_VIDEOS
